import { takeUntil } from 'rxjs/operators';
import { Component, OnInit, OnDestroy, Inject, LOCALE_ID, } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { SpinnerComponent } from './core/spinner/spinner.component';
import { CartService } from './providers/services/cart.service';
import { NgcCookieConsentService, NgcStatusChangeEvent } from 'ngx-cookieconsent';
import { environment } from '../environments/environment';
import { MenuService } from './providers/services/menu.service';
import { Subject } from 'rxjs';
import { PlatformToastMessage } from '@pdp/common';
import { PlatformAwareService } from './providers/services/platform-aware.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RequestUtils } from './common/request-utils';


@Component({
    selector: 'pdp-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet, SpinnerComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  public _appVersion = environment.appVersion;

  private unsubscribe: Subject<void> = new Subject();
  public static toastSubject = new Subject<PlatformToastMessage>();

  constructor(
    @Inject(LOCALE_ID) protected localeId: string,
    public menuService: MenuService,
    protected _snackBar: MatSnackBar,
    private platformAwareService: PlatformAwareService,
    public cartService: CartService,
    private ccService: NgcCookieConsentService) {
    RequestUtils.logRequest('App Component initialized');
    RequestUtils.logRequest('Streamlined HQ!', { color: '#12675e', 'font-size': '32px', padding: '30px 0px 30px 15px', 'font-weight': '800' });
    AppComponent.toastSubject.pipe(takeUntil(this.unsubscribe)).subscribe((message: PlatformToastMessage) => {
      RequestUtils.logRequest('Displaying snackbar message', { message });
      this.doDisplaySnackBarMessage(message);
    });
  }

  public doDisplaySnackBarMessage(message: PlatformToastMessage) {
    this._snackBar.open(message.message, message.title, {
      data: message,
      duration: 6 * 1000,
      // panelClass: ['custom-snackbar-success']
    });
  }

  ngOnInit() {
    this.menuService.fetchHorizontalMenuItems();
    this.menuService.fetchVerticalMenuItems();
    this.menuService.fetchFooterMenuItems();

    if (this.platformAwareService.isBrowser) {
      this.cartService.getOrder();
    }

    if (this.platformAwareService.isBrowser) {
      const userAgent = navigator.userAgent;
      const isRobot = this.detectRobot(userAgent);
      if (!isRobot) {
        this.cookieConsentInit();
      } else {
        this.ccService.destroy();
      }
    }
  }

  private cookieConsentInit() {
    if (environment.profile === 'local') {
      RequestUtils.logRequest('Skippin Cookie consent in local profile. Remove return here if you need to work on cookie consent');
      this.ccService.destroy();
      return;
    }


    if (this.ccService.hasConsented()) {
      this.ccService.toggleRevokeButton(true);
    }

    // subscribe to cookieconsent observables to react to main events
    this.ccService.popupOpen$.pipe(takeUntil(this.unsubscribe)).subscribe(
      () => {
        // you can use this.ccService.getConfig() to do stuff...
      });

    this.ccService.popupClose$.pipe(takeUntil(this.unsubscribe)).subscribe(
      () => {
        // you can use this.ccService.getConfig() to do stuff...
      });

    // this.ccService.initialize$.pipe(takeUntil(this.unsubscribe)).subscribe(
    //   (event: NgcInitializeEvent) => {
    //     // you can use this.ccService.getConfig() to do stuff...
    //   });

    this.ccService.statusChange$.pipe(takeUntil(this.unsubscribe)).subscribe(
      (event: NgcStatusChangeEvent) => {
        // you can use this.ccService.getConfig() to do stuff...
        if (event.status === 'dismiss' && this.ccService.hasConsented()) {
          this.ccService.toggleRevokeButton(true);
          this.ccService.fadeOut();
        }
      });

    this.ccService.revokeChoice$.pipe(takeUntil(this.unsubscribe)).subscribe(
      () => {
        // you can use this.ccService.getConfig() to do stuff...
      });
  }

  private detectRobot = (userAgent: string): boolean => {
    const robots = new RegExp(([
      /bot/, /spider/, /crawl/,                               // GENERAL TERMS
      /APIs-Google/, /AdsBot/, /Googlebot/,                   // GOOGLE ROBOTS
      /mediapartners/, /Google Favicon/,
      /FeedFetcher/, /Google-Read-Aloud/,
      /Lighthouse/,                                           //lighthouse google page speed
      /DuplexWeb-Google/, /googleweblight/,
      /bing/, /yandex/, /baidu/, /duckduck/, /yahoo/,           // OTHER ENGINES
      /ecosia/, /ia_archiver/,
      /facebook/, /instagram/, /pinterest/, /reddit/,          // SOCIAL MEDIA
      /slack/, /twitter/, /whatsapp/, /youtube/,
      /semrush/,                                            // OTHER
    ] as RegExp[]).map((r) => r.source).join("|"), "i");     // BUILD REGEXP + "i" FLAG
    return robots.test(userAgent);
  };

  ngOnDestroy() {
    // unsubscribe to cookieconsent observables to prevent memory leaks
    this.unsubscribe.next(undefined);
    this.unsubscribe.complete();
  }
}
